import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Loader,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { notifyError, notifySuccess, notifyWarning } from "../../utils/notify";
import { useServicePreAppointmentDocuments } from "../../context/ServicePreAppointmentDocumentsContext";
import {
  PendingPreAppointmentDocument,
  PreAppointmentDocumentAttachment,
} from "../../types/servicePreAppointment";
import { AssignPreAppointmentDocumentModal } from "./AssignPreAppointmentDocumentModal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { makeTempId } from "../../utils/makeTempId";

interface ServicePreAppointmentDocumentsSectionProps {
  salonServiceId: string;
  readOnly?: boolean;
  pendingDocuments?: PendingPreAppointmentDocument[];
  onAddPendingDocument?: (doc: PendingPreAppointmentDocument) => void;
  onRemovePendingDocument?: (tempId: string) => void;
}

/**
 * Per-service "Pre-appointment documents" config — the pre-visit sibling of
 * RequiredFormsSection. Attached documents appear in the client's pre-visit
 * forms portal (view-only) and the pre-visit SMS mentions them.
 */
export const ServicePreAppointmentDocumentsSection: React.FC<
  ServicePreAppointmentDocumentsSectionProps
> = ({
  salonServiceId,
  readOnly = false,
  pendingDocuments,
  onAddPendingDocument,
  onRemovePendingDocument,
}) => {
  const {
    listPreAppointmentDocuments,
    linkPreAppointmentDocument,
    unlinkPreAppointmentDocument,
  } = useServicePreAppointmentDocuments();

  const [attachments, setAttachments] = useState<
    PreAppointmentDocumentAttachment[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [pendingUnlink, setPendingUnlink] = useState<{
    attachment: PreAppointmentDocumentAttachment;
    name: string;
  } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const refresh = useCallback(async () => {
    if (!salonServiceId) return;
    setIsLoading(true);
    try {
      const list = await listPreAppointmentDocuments(salonServiceId);
      setAttachments(list);
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not load pre-appointment documents",
      });
    } finally {
      setIsLoading(false);
    }
  }, [salonServiceId, listPreAppointmentDocuments]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Documents already attached (saved + unsaved pending) — hidden in picker.
  const attachedIds = useMemo(() => {
    const ids = new Set<string>();
    attachments.forEach((a) => ids.add(a.source_id));
    pendingDocuments?.forEach((pd) => ids.add(pd.source_id));
    return ids;
  }, [attachments, pendingDocuments]);

  const handleLink = async (
    picks: Array<{ source_id: string; name: string }>,
  ) => {
    if (picks.length === 0) return;
    if (onAddPendingDocument && !salonServiceId) {
      picks.forEach((pick) => {
        onAddPendingDocument({
          tempId: makeTempId("tmp"),
          source_type: "salon_document",
          source_id: pick.source_id,
          name: pick.name,
        });
      });
      setPickerOpen(false);
      return;
    }
    setIsLinking(true);
    try {
      // Attempt every link so one failure doesn't abandon the rest; always
      // refresh() so attachedIds reflects whatever actually got linked.
      const results = await Promise.allSettled(
        picks.map((pick) =>
          linkPreAppointmentDocument(salonServiceId, {
            source_type: "salon_document",
            source_id: pick.source_id,
          }),
        ),
      );
      const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected",
      );
      const successCount = results.length - failures.length;

      if (failures.length === 0) {
        notifySuccess(
          picks.length > 1
            ? `${picks.length} documents will be shared before this service.`
            : "This document will be shared before this service.",
          {
            title:
              picks.length > 1
                ? "Pre-appointment documents attached"
                : "Pre-appointment document attached",
          },
        );
        setPickerOpen(false);
      } else if (successCount === 0) {
        const reason = failures[0].reason;
        notifyError(
          reason instanceof Error ? reason.message : "Unknown error.",
          { title: "Could not attach documents" },
        );
      } else {
        notifyWarning(
          `Attached ${successCount} of ${picks.length}. ${failures.length} could not be attached — please retry the remaining.`,
          { title: "Some documents were not attached" },
        );
      }
    } finally {
      await refresh();
      setIsLinking(false);
    }
  };

  const confirmUnlink = async () => {
    if (!pendingUnlink) return;
    setIsUnlinking(true);
    try {
      await unlinkPreAppointmentDocument(
        salonServiceId,
        pendingUnlink.attachment.uuid,
      );
      notifySuccess("The document was detached from this service.", {
        title: "Pre-appointment document removed",
      });
      setPendingUnlink(null);
      await refresh();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : "Unknown error.", {
        title: "Could not remove document",
      });
    } finally {
      setIsUnlinking(false);
    }
  };

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="center">
        <Box>
          <Title order={6}>Pre-appointment documents</Title>
          <Text size="xs" c="dimmed">
            Documents clients can review before this service. Shared via the
            pre-visit text message.
          </Text>
        </Box>
        {!readOnly && (
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() => setPickerOpen(true)}
          >
            Add document
          </Button>
        )}
      </Group>

      {isLoading ? (
        <Group justify="center" py="sm">
          <Loader size="xs" />
        </Group>
      ) : attachments.length === 0 &&
        (!pendingDocuments || pendingDocuments.length === 0) ? (
        <Alert
          color="gray"
          icon={<IconAlertCircle size={14} />}
          variant="light"
        >
          No pre-appointment documents yet. Add one to share it with clients
          before their visit.
        </Alert>
      ) : (
        <Stack gap="xs">
          {attachments.map((attachment) => (
            <Card key={attachment.uuid} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {attachment.name || attachment.source_id}
                  </Text>
                  <Badge size="xs" variant="light" color="blue">
                    Document
                  </Badge>
                </Group>
                {!readOnly && (
                  <Tooltip label="Remove">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() =>
                        setPendingUnlink({
                          attachment,
                          name: attachment.name || attachment.source_id,
                        })
                      }
                      aria-label="Remove pre-appointment document"
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            </Card>
          ))}
          {pendingDocuments?.map((pd) => (
            <Card key={pd.tempId} withBorder padding="xs" radius="sm">
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {pd.name}
                  </Text>
                  <Badge size="xs" variant="light" color="blue">
                    Document
                  </Badge>
                </Group>
                {onRemovePendingDocument && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemovePendingDocument(pd.tempId)}
                    aria-label="Remove pending document"
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      <AssignPreAppointmentDocumentModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAssign={handleLink}
        isAssigning={isLinking}
        existingDocumentIds={attachedIds}
      />

      <DeleteConfirmationModal
        isOpen={pendingUnlink !== null}
        onClose={() => {
          if (!isUnlinking) setPendingUnlink(null);
        }}
        onConfirm={confirmUnlink}
        title="Remove pre-appointment document"
        message={
          pendingUnlink
            ? `Stop sharing "${pendingUnlink.name}" before this service?`
            : ""
        }
        isLoading={isUnlinking}
      />
    </Stack>
  );
};

export default ServicePreAppointmentDocumentsSection;
